/**
 * KuenstlerinSubPage
 * Renders one of the four pages inside the Künstlerin section.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useArtworks } from '@/hooks/useArtworks'
import { Markdown } from '@/components/ui/Markdown'
import { Seo } from '@/components/Seo'
import { useT, STRINGS, useLanguage, useFieldLang } from '@/context/LanguageContext'
import './ContentPage.css'

export function KuenstlerinSubPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { getSection, loading } = useArtworks()
  const section = getSection('kuenstlerin')
  const page = section?.pages?.find((p) => p.slug === slug)
  const t = useT()
  const { lang } = useLanguage()
  const pick = useFieldLang()

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>{t(STRINGS.common.loading)}</p>
      </div>
    )
  }

  if (!page) {
    return (
      <div className="error-screen">
        <p>{t(STRINGS.common.notFound)}</p>
        <button onClick={() => navigate('/kuenstlerin')}>
          {t(STRINGS.common.backToArtist)}
        </button>
      </div>
    )
  }

  const title = pick(page.title, page.title_en) || page.title
  const description = pick(page.description, page.description_en) || page.description
  const isFallback = lang === 'en' && !page.description_en

  return (
    <article className="content-page">
      <Seo
        type="article"
        title={title}
        description={description
          .replace(/^#+\s.*$/gm, '')
          .replace(/[*_>`]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 200)}
      />
      <div className="content-container content-container-prose">
        <button className="back-button" onClick={() => navigate('/kuenstlerin')}>
          ← {t(STRINGS.common.backToArtist)}
        </button>
        <header className="content-header content-header-prose">
          <h1>{title}</h1>
        </header>
        {isFallback && (
          <p className="content-lang-notice">{t(STRINGS.common.contentGermanOnly)}</p>
        )}
        <Markdown className="content-prose">{description}</Markdown>
      </div>
    </article>
  )
}
